/**
 * Route Protection Utilities
 *
 * Helper functions for checking route access, determining default routes,
 * and handling unauthorized access redirects.
 */

import { OrgRole } from '../../../pure-js/types/OrganizationMembership'
import { isRouteAllowed, getRouteType } from '../config/routes/routeConfig'
import { GamerRoutes, FacilitatorRoutes } from '../config/routes/routeEnums'

/**
 * Check if a user can access a specific route
 */
export const canAccessRoute = (path: string, role: OrgRole): boolean => {
  return isRouteAllowed(path, role)
}

/**
 * Get the default route for a user role
 */
export const getDefaultRouteForRole = (role: OrgRole): string => {
  switch (role) {
    case OrgRole.FACILITATOR:
    case OrgRole.ADMIN:
      return FacilitatorRoutes.BASE
    case OrgRole.GAMER:
    case OrgRole.LEADER:
    default:
      return GamerRoutes.BASE
  }
}

/**
 * Get the redirect path for an unauthorized user attempting to access a route
 */
export const redirectUnauthorizedUser = (role: OrgRole, _attemptedPath: string): string => {
  return getDefaultRouteForRole(role)
}

/**
 * Check if a route is a shared route (accessible by both gamer and facilitator)
 */
export const isSharedRoute = (path: string): boolean => {
  const routeType = getRouteType(path)
  return routeType === 'shared'
}

/**
 * Check if a route is a facilitator-only route
 */
export const isFacilitatorRoute = (path: string): boolean => {
  const routeType = getRouteType(path)
  return routeType === 'facilitator'
}

/**
 * Check if a route is a gamer-only route
 */
export const isGamerRoute = (path: string): boolean => {
  const routeType = getRouteType(path)
  return routeType === 'gamer'
}

/**
 * Check if a route is a public route (no authentication required)
 */
export const isPublicRoute = (path: string): boolean => {
  const routeType = getRouteType(path)
  return routeType === 'public'
}

/**
 * Get the appropriate route path for a shared route based on user role
 * For example, game-center should be /gamer/game-center for gamers and /facilitator/game-center-facilitator for facilitators
 */
export const getRoleSpecificRoute = (sharedRoutePath: string, userRole: OrgRole): string => {
  const routeType = getRouteType(sharedRoutePath)
  if (routeType !== 'shared') return sharedRoutePath

  const isFacilitatorOrAdmin = userRole === OrgRole.FACILITATOR || userRole === OrgRole.ADMIN

  if (sharedRoutePath.includes('/game-center')) {
    return isFacilitatorOrAdmin ? '/facilitator/game-center-facilitator' : '/gamer/game-center'
  }
  if (sharedRoutePath.includes('/play-game-with-introduction')) {
    const base = isFacilitatorOrAdmin ? '/facilitator' : '/gamer'
    return `${base}/play-game-with-introduction`
  }
  if (sharedRoutePath.includes('/play-game-without-introduction')) {
    const base = isFacilitatorOrAdmin ? '/facilitator' : '/gamer'
    return `${base}/play-game-without-introduction`
  }
  return sharedRoutePath
}
